import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { returnHomeService } from "@/features/return-home/services/returnHomeService";
import { companyService } from "@/features/company/services/companyService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { useFormatDate } from "@/shared/hooks/useFormatDate";
import type {
  ReturnHomeRecord,
  ReturnHomeStatus,
  ReturnHomeApprovalStatus,
} from "@/features/return-home/types/returnHome";
import type { CompanyOption } from "@/features/inquiries/types/inquiry";
import { ReturnHomeDetailPanel } from "@/features/return-home/components/ReturnHomeDetailPanel";

const OFFICER_TYPES = ["officer", "admin"];

const APPROVAL_TABS: Array<ReturnHomeApprovalStatus | "All"> = [
  "All",
  "draft",
  "pending",
  "approved",
  "rejected",
  "cancelled",
];

type SortKey =
  | "user_name"
  | "flight_date"
  | "return_date"
  | "route_origin"
  | "route_destination"
  | "ticket_type"
  | "approval_status"
  | "created_at";
type SortDir = "asc" | "desc";

const computeStatus = (record: ReturnHomeRecord): ReturnHomeStatus => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const flight = record.flight_date ? new Date(record.flight_date) : null;
  const returnDt = record.return_date ? new Date(record.return_date) : null;

  if (!flight) return "";
  if (flight) flight.setHours(0, 0, 0, 0);
  if (returnDt) returnDt.setHours(0, 0, 0, 0);

  if (today < flight) return "Upcoming";
  if (returnDt && today > returnDt) return "Returned";
  if (today >= flight) return "Out of Country";
  return "";
};

const travelStatusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  Upcoming: "info",
  "Out of Country": "warning",
  Returned: "success",
};

const APPROVAL_VARIANT: Record<
  ReturnHomeApprovalStatus,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  draft: "default",
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "default",
};

const STATUS_I18N_MAP: Record<string, string> = {
  Upcoming: "return_home.status.upcoming",
  "Out of Country": "return_home.status.out_of_country",
  Returned: "return_home.status.returned",
};

const getReturnTypeLabel = (returnType?: number | null) =>
  returnType === 0 ? "Paid Leave" :
  returnType === 1 ? "Resignation" :
  returnType === 2 ? "Resignation + Lumpsum" :
  returnType === 3 ? "No Paid Leave" :
  returnType === 4 ? "Finished Contract" : "";

export const ReturnHomeListPage = () => {
  const { t } = useTranslation();
  const { fmtDate } = useFormatDate();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(
    userData?.userType?.toLowerCase() || "",
  );

  const [records, setRecords] = useState<ReturnHomeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  // Filters
  const [approvalFilter, setApprovalFilter] = useState<
    ReturnHomeApprovalStatus | "All"
  >("All");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchUserName, setSearchUserName] = useState("");
  const [searchFlightFrom, setSearchFlightFrom] = useState("");
  const [searchFlightTo, setSearchFlightTo] = useState("");
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  // Travel status filter
  const [travelStatusFilter, setTravelStatusFilter] = useState<string>("");

  // Multi-select company filter
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Detail panel
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [selectedCommentCount, setSelectedCommentCount] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);

  // Load company dropdown for officers
  useEffect(() => {
    if (!isOfficer) return;
    companyService
      .getDropdown("flight_tracker")
      .then(setCompanies)
      .catch(() => {});
  }, [isOfficer]);

  // Close company dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) setCompanyDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      const filters: Record<string, string> = {};

      if (approvalFilter !== "All")
        filters.status =
          approvalFilter.charAt(0).toUpperCase() + approvalFilter.slice(1);
      if (isOfficer) {
        if (searchUserName) filters.user_name = searchUserName;
        if (searchFlightFrom) filters.flight_date_from = searchFlightFrom;
        if (searchFlightTo) filters.flight_date_to = searchFlightTo;
      }

      const data = await returnHomeService.search(filters);
      setRecords(data);
    } catch (error) {
      console.error("Failed to load return home records:", error);
    } finally {
      setLoading(false);
    }
  }, [
    approvalFilter,
    isOfficer,
    searchUserName,
    searchFlightFrom,
    searchFlightTo,
  ]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // Auto-open panel when navigated from a notification
  useEffect(() => {
    const openId = (location.state as { openId?: string } | null)?.openId;
    if (!openId) return;
    setSelectedRecordId(openId);
    setDetailOpen(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "created_at" ? "desc" : "asc");
    }
  };

  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const aVal = String(
        (a as unknown as Record<string, unknown>)[sortKey] || "",
      ).toLowerCase();
      const bVal = String(
        (b as unknown as Record<string, unknown>)[sortKey] || "",
      ).toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [records, sortKey, sortDir]);

  const handleCompanyToggle = (id: string) => setCompanyFilter(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  const filteredRecords = useMemo(() => {
    let result = sortedRecords;
    if (travelStatusFilter) {
      result = result.filter(rec =>
        computeStatus(rec) === travelStatusFilter &&
        (rec.status?.toLowerCase() as ReturnHomeApprovalStatus) !== "rejected"
      );
    }
    if (companyFilter.length === 0) return result;
    const selectedLabels = companies.filter(c => companyFilter.includes(c.value)).map(c => c.label.toLowerCase());
    return result.filter(rec => selectedLabels.includes((rec.company_name_text || "").toLowerCase()));
  }, [sortedRecords, travelStatusFilter, companyFilter, companies]);

  useEffect(() => { setPage(0); }, [travelStatusFilter, companyFilter, searchUserName, searchFlightFrom, searchFlightTo, approvalFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / 50));
  const pagedRecords = filteredRecords.slice(page * 50, (page + 1) * 50);

  const clearSearch = () => {
    setTravelStatusFilter("");
    setCompanyFilter([]);
    setCompanySearch("");
    setSearchUserName("");
    setSearchFlightFrom("");
    setSearchFlightTo("");
  };

  const exportToCSV = () => {
    const headers = [
      "ID",
      "User Name",
      "First Name",
      "Last Name",
      "Company",
      "Business Unit",
      "Visa Type",
      "Joining Date",
      "Visa Expiry Date",
      "Flight Date",
      "Return Date",
      "Route Origin",
      "Route Destination",
      "Ticket Type",
      "Category",
      "TIO / JO",
      "Details",
      "Resignation Date",
      "Leave Days",
      "Mode of Payment",
      "Payment Amount",
      "Currency",
      "Payment Settled",
      "Travel Status",
      "Approval Status",
      "Approved By",
      "Approved At",
      "Approver Remarks",
      "Cancelled By",
      "Cancelled At",
      "Cancellation Reason",
      "Application Date",
      "Created At",
      "Comment Count",
      "Attachments Count",
    ];

    const rows = filteredRecords.map((rec) => {
      const travelStatus = computeStatus(rec);
      const approval =
        (rec.status?.toLowerCase() as ReturnHomeApprovalStatus) || "draft";
      return [
        rec.id,
        rec.user_name || "",
        rec.first_name || "",
        rec.last_name || "",
        rec.company_name_text || "",
        rec.business_unit || "",
        rec.visa_type_descr || rec.visa_type || "",
        rec.joining_date ? fmtDate(rec.joining_date) : "",
        rec.visa_expiry_date ? fmtDate(rec.visa_expiry_date) : "",
        rec.flight_date ? fmtDate(rec.flight_date) : "",
        rec.return_date ? fmtDate(rec.return_date) : "",
        rec.route_origin || "",
        rec.route_destination || "",
        rec.ticket_type ? getTicketTypeLabel(rec.ticket_type) : "",
        getReturnTypeLabel(rec.return_type),
        rec.tio_jo || "",
        rec.details || "",
        rec.resign_date ? fmtDate(rec.resign_date) : "",
        rec.leave_days != null ? String(rec.leave_days) : "",
        rec.mode_of_payment || "",
        rec.payment_amount != null ? String(rec.payment_amount) : "",
        rec.currency || "",
        rec.mode_of_payment === "company" &&
        rec.payment_amount != null &&
        Number(rec.payment_amount) > 0
          ? rec.payment_settled
            ? "Yes"
            : "No"
          : "",
        travelStatus,
        approval,
        rec.approved_by || "",
        rec.approved_at ? fmtDate(rec.approved_at) : "",
        rec.approver_remarks || "",
        rec.cancelled_by_name || rec.cancelled_by || "",
        rec.cancelled_at ? fmtDate(rec.cancelled_at) : "",
        rec.cancellation_reason || "",
        rec.application_date
          ? fmtDate(rec.application_date)
          : rec.created_at
            ? fmtDate(rec.created_at)
            : "",
        rec.created_at ? fmtDate(rec.created_at) : "",
        String(rec.comment_count ?? 0),
        String(rec.attachments?.length ?? 0),
      ];
    });

    const escape = (val: unknown) => {
      const str = val == null ? "" : String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };
    const csv = [headers, ...rows]
      .map((row) => row.map(escape).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `flight-tracker-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const SortArrow = ({ column }: { column: SortKey }) => {
    if (sortKey !== column)
      return <span className="ml-1 text-gray-300">&#8597;</span>;
    return <span className="ml-1">{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  const getStatusDaysMessage = (record: ReturnHomeRecord): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const flight = record.flight_date ? new Date(record.flight_date) : null;
    const returnDt = record.return_date ? new Date(record.return_date) : null;
    if (!flight) return "";
    flight.setHours(0, 0, 0, 0);
    if (returnDt) returnDt.setHours(0, 0, 0, 0);

    if (today < flight) {
      const days = Math.ceil(
        (flight.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );
      return t("return_home.status.days_until_departure", { count: days });
    }
    if (returnDt && today > returnDt) return "";
    if (returnDt && today >= flight) {
      const days = Math.ceil(
        (returnDt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );
      return t("return_home.status.days_until_return", { count: days });
    }
    return "";
  };

  const getTicketTypeLabel = (type: string | undefined): string => {
    if (!type) return "-";
    if (type === "direct" || type === "layover")
      return t(`return_home.form.ticket_${type}`);
    return type;
  };

  if (loading && records.length === 0) return <Spinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          {t("return_home.title")}
        </h1>
        <div className="flex gap-2">
          {isOfficer && (
            <Button
              variant="secondary"
              onClick={exportToCSV}
              disabled={filteredRecords.length === 0}
              className="flex-1 md:flex-none"
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {t("return_home.search.export_csv")}
            </Button>
          )}
          {isOfficer && (
            <Button
              variant="secondary"
              onClick={() => navigate("/return-home/audit")}
              className="flex-1 md:flex-none"
            >
              {t("audit.title")}
            </Button>
          )}
          <Button
            onClick={() => navigate("/return-home/new")}
            className="flex-1 md:flex-none"
          >
            {t("return_home.new_application")}
          </Button>
        </div>
      </div>

      {/* Non-officer approval filter */}
      {!isOfficer && (
        <div className="flex gap-2 mb-4 flex-wrap">
          {APPROVAL_TABS.map((status) => (
            <button
              key={status}
              onClick={() => setApprovalFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap active:scale-95 ${
                approvalFilter === status
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t(`return_home.approval.status_${status === "All" ? "all" : status}`)}
            </button>
          ))}
        </div>
      )}

      {/* Officer Search Panel */}
      {isOfficer && (
        <div className="mb-4">
          <button
            onClick={() => setSearchOpen((o) => !o)}
            className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 active:text-indigo-700 transition-colors"
          >
            <svg
              className={`w-4 h-4 transition-transform ${searchOpen ? "rotate-90" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            {t("return_home.search.title")}
          </button>

          {searchOpen && (
            <div className="mt-3 p-4 bg-white rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("return_home.search.travel_status")}
                  </label>
                  <select
                    value={travelStatusFilter}
                    onChange={(e) => {
                      setTravelStatusFilter(e.target.value);
                      if (
                        e.target.value !== "" &&
                        ["rejected", "cancelled"].includes(approvalFilter)
                      ) {
                        setApprovalFilter("All");
                      }
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("return_home.status.all")}</option>
                    <option value="Upcoming">{t("return_home.status.upcoming")}</option>
                    <option value="Out of Country">{t("return_home.status.out_of_country")}</option>
                    <option value="Returned">{t("return_home.status.returned")}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("return_home.search.approval_status")}
                  </label>
                  <select
                    value={approvalFilter}
                    onChange={(e) => setApprovalFilter(e.target.value as ReturnHomeApprovalStatus | "All")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {APPROVAL_TABS.filter(
                      (status) =>
                        travelStatusFilter === "" ||
                        !["rejected", "cancelled"].includes(status),
                    ).map((status) => (
                      <option key={status} value={status}>
                        {t(`return_home.approval.status_${status === "All" ? "all" : status}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("return_home.search.company")}
                  </label>
                  {companyFilter.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {companyFilter.map(val => {
                        const match = companies.find(c => c.value === val);
                        return (
                          <span key={val} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {match?.label || val}
                            <button type="button" onClick={() => handleCompanyToggle(val)} className="hover:text-indigo-900">&times;</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="relative" ref={companyDropdownRef}>
                    <input
                      type="text"
                      value={companySearch}
                      onChange={e => { setCompanySearch(e.target.value); if (!companyDropdownOpen) setCompanyDropdownOpen(true); }}
                      onFocus={() => setCompanyDropdownOpen(true)}
                      placeholder={t("feed.form.search_company", "Search company...")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {companyDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                        {companies.filter(c => c.label?.toLowerCase().includes(companySearch.toLowerCase())).map(c => {
                          const isSelected = companyFilter.includes(c.value);
                          return (
                            <button key={c.value} type="button" onClick={() => { handleCompanyToggle(c.value); setCompanySearch(""); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}>
                              <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                                {isSelected && "✓"}
                              </span>
                              {c.label}
                            </button>
                          );
                        })}
                        {companies.filter(c => c.label?.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && (
                          <p className="px-3 py-2 text-xs text-gray-400 italic">{t("common.no_results", "No results found")}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("return_home.search.user_name")}
                  </label>
                  <input
                    type="text"
                    value={searchUserName}
                    onChange={(e) => setSearchUserName(e.target.value)}
                    placeholder={t("return_home.search.user_name_placeholder")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("return_home.search.flight_date_from")}
                  </label>
                  <input
                    type="date"
                    value={searchFlightFrom}
                    onChange={(e) => setSearchFlightFrom(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("return_home.search.flight_date_to")}
                  </label>
                  <input
                    type="date"
                    value={searchFlightTo}
                    onChange={(e) => setSearchFlightTo(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-3">
                <Button variant="ghost" size="sm" onClick={clearSearch}>
                  {t("common.clear_all")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : filteredRecords.length === 0 ? (
        <EmptyState title={t("return_home.no_results")} />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs text-gray-500">
              {t("settings.search_showing", {
                from: page * 50 + 1,
                to: Math.min((page + 1) * 50, filteredRecords.length),
                total: filteredRecords.length,
              })}
            </p>
          </div>
          <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap w-12">
                  #
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("user_name")}
                >
                  {t("return_home.table.name")} <SortArrow column="user_name" />
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("flight_date")}
                >
                  {t("return_home.table.flight_date")}{" "}
                  <SortArrow column="flight_date" />
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("return_date")}
                >
                  {t("return_home.table.return_date")}{" "}
                  <SortArrow column="return_date" />
                </th>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                  {t("return_home.table.route")}
                </th>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                  {t("return_home.table.travel_status")}
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("approval_status")}
                >
                  {t("return_home.table.approval")}{" "}
                  <SortArrow column="approval_status" />
                </th>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                  {t("return_home.table.flags")}
                </th>
                <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                  {t("return_home.table.payment_settled")}
                </th>
                <th
                  className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort("created_at")}
                >
                  {t("return_home.table.applied")}{" "}
                  <SortArrow column="created_at" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedRecords.map((rec) => {
                const travelStatus = computeStatus(rec);
                const statusMsg = getStatusDaysMessage(rec);
                const approval =
                  (rec.status?.toLowerCase() as ReturnHomeApprovalStatus) ||
                  "draft";

                return (
                  <tr
                    key={rec.id}
                    onClick={() => { setSelectedRecordId(rec.id); setSelectedCommentCount(rec.comment_count ?? 0); setDetailOpen(true); }}
                    className={`hover:bg-gray-50 cursor-pointer ${rec.return_type === 2 ? "bg-red-50/40" : ""}`}
                  >
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                      #{rec.id}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{rec.user_name || "-"}</span>
                        <div className="text-xs text-gray-500 font-normal">
                          {rec.company_name_text || t("common.n_a")}
                        </div>
                        <span className="flex items-center gap-1.5 text-xs text-gray-400">
                          {(rec.comment_count ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-0.5">
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                />
                              </svg>
                              {rec.comment_count}
                            </span>
                          )}
                          {(rec.attachments?.length ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-0.5">
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                />
                              </svg>
                              {rec.attachments!.length}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {rec.flight_date ? fmtDate(rec.flight_date) : "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {rec.return_date ? fmtDate(rec.return_date) : "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {rec.route_origin && rec.route_destination
                        ? `${rec.route_origin.trim()} → ${rec.route_destination.trim()}`
                        : "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {["rejected", "cancelled"].includes(approval) ? (
                        <span className="text-gray-300">-</span>
                      ) : travelStatus ? (
                        <div>
                          <Badge
                            variant={
                              travelStatusVariant[travelStatus] || "default"
                            }
                          >
                            {t(STATUS_I18N_MAP[travelStatus] || travelStatus)}
                          </Badge>
                          {statusMsg && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {statusMsg}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={APPROVAL_VARIANT[approval]}>
                        {t(`return_home.approval.status_${approval}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {["rejected", "cancelled"].includes(approval) ? (
                        <span className="text-gray-300">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {rec.return_type === 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              {t("return_home.form.lumpsum")}
                            </span>
                          )}
                          {(rec.return_type === 1 || rec.return_type === 2) && (
                            <div className="flex flex-col gap-0.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                                {t("return_home.form.resignation_short")}
                              </span>
                              {rec.resign_date && (
                                <span className="text-xs text-gray-500 pl-1">
                                  {t("return_home.table.resign_date_label")}:{" "}
                                  {fmtDate(rec.resign_date)}
                                </span>
                              )}
                            </div>
                          )}
                          {rec.return_type === 0 && (
                            <div className="flex flex-col gap-0.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                {t("return_home.form.paid_leave_short")}
                              </span>
                              {rec.leave_days != null && (
                                <span className="text-xs text-gray-500 pl-1">
                                  {rec.leave_days}{" "}
                                  {t("return_home.table.days_label")}
                                </span>
                              )}
                            </div>
                          )}
                          {rec.return_type === 4 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                              {t("return_home.form.category_finished_contract")}
                            </span>
                          )}
                          {rec.mode_of_payment && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                rec.mode_of_payment === "company" || rec.mode_of_payment === "svo_advanced"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {t(
                                `return_home.form.payment_${rec.mode_of_payment}`,
                                { defaultValue: rec.mode_of_payment === "svo_advanced" ? "Request ticket to SVO, to be billed to the company." : rec.mode_of_payment },
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(rec.mode_of_payment === "company" || rec.mode_of_payment === "svo_advanced") &&
                      rec.payment_amount != null &&
                      Number(rec.payment_amount) > 0 ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                            rec.payment_settled
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {rec.payment_settled ? "✅" : "⏳"}
                          {rec.payment_settled
                            ? t("return_home.form.payment_settled_yes")
                            : t("return_home.form.payment_settled_no")}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {rec.application_date
                        ? fmtDate(rec.application_date)
                        : fmtDate(rec.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                {t("settings.page_prev")}
              </Button>
              <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                {t("settings.page_next")}
              </Button>
            </div>
          )}
        </div>
      )}

      <ReturnHomeDetailPanel
        recordId={selectedRecordId}
        initialCommentCount={selectedCommentCount}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onRefresh={loadRecords}
      />
    </div>
  );
};
